import { useMutation } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { meApi } from "../../api/me";
import type { UserOut } from "../../api/types";
import { errorMessage } from "../../shared/errors";
import { useInvalidateMe } from "../../shared/hooks/useMe";
import { Alert, Button, Card, Input, Modal } from "../../ui";

/** The delete form itself; shown at the foot of Profile behind a disclosure (docs/15 §2). */
export function DeleteAccount() {
  const me = useOutletContext<UserOut>();
  const navigate = useNavigate();
  const invalidateMe = useInvalidateMe();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const del = useMutation({
    mutationFn: () =>
      meApi.deleteAccount(me.has_password ? { password } : { confirm }),
    onSuccess: async () => {
      await invalidateMe();
      navigate("/logout");
    },
    onError: (err) => {
      setOpen(false);
      setError(errorMessage(err));
    },
  });
  return (
    <>
      <p className="mb-4 text-sm text-slate-700">
        This deletes your Intelibo account and your data in every Intelibo
        product. Unused purchased credits from the last 14 days are refunded;
        other balances are lost.
      </p>
      {error ? <Alert kind="error">{error}</Alert> : null}
      <form
        className="mt-4 flex max-w-md flex-col gap-4"
        aria-label="Delete account"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          setOpen(true);
        }}
      >
        {me.has_password ? (
          <Input
            label="Your password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        ) : (
          <Input
            label='Type "DELETE" to confirm'
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        )}
        <Button variant="danger" type="submit">
          Delete my account
        </Button>
      </form>
      <Modal
        open={open}
        title="Delete your account?"
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => del.mutate()}
              loading={del.isPending}
            >
              Delete
            </Button>
          </>
        }
      >
        This cannot be undone.
      </Modal>
    </>
  );
}

/** `/account/delete` still works for anyone holding the link. */
export function DeleteAccountPage() {
  return (
    <Card>
      <h1 className="mb-4 text-xl font-semibold">Delete account</h1>
      <DeleteAccount />
    </Card>
  );
}
